
import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '@shared/lib/components/ui/icon/icon.component';
import { IconCustomComponent } from '@shared/lib/components/ui/icon-ods/icon.component';
import { isNotNullOrEmpty, isNullOrEmpty } from '@shared/lib/core/utils/data-validation.util';
import { DeploymentStore } from '@shared/lib/stores/deployment.store';
import { UserStore } from '@shared/lib/stores/user.store';
import { navLinks } from '@web-app/app/components/layout/sidebar/routes';
import { TenantDeploymentComponent } from '@web-app/app/dialogs/tenant-deployment/tenant-deployment.component';
import { ButtonDirective } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    ButtonDirective,
    IconComponent,
    IconCustomComponent,
    ImageModule,
    MenuModule,
    RouterModule,
    TooltipModule,
    TranslatePipe,
    TenantDeploymentComponent
],
    selector: 'ods-layout-sidebar',
    styleUrl: './sidebar.component.css',
    templateUrl: './sidebar.component.html'
})
export class SideBarComponent {
  userStore = inject(UserStore);
  deploymentStore = inject(DeploymentStore);
  router = inject(Router);

  readonly onLogout = output();
  isTenantDeploymentModalVisible = signal(false);
  tenantModalRequireDeployment = signal(true);
  isSideNavCollapsed = computed(() => this.userStore.isSideNavCollapsed());
  roleBasedNavLinks = computed(() => {
    return navLinks.map(navLink => ({
      ...navLink,
      subItems: navLink.subItems.filter(subItem => this.userStore.hasUserRole(subItem.role)),
    })).filter(navLink => navLink.subItems.length > 0);
  });

  toggleSideNav = () => {
    this.userStore.toggleSideNav();
  };

  logout = () => {
    this.onLogout.emit();
  };

  isLinkValid(link: string | null): boolean {
    if (isNullOrEmpty(link)) {
      return false;
    }
    const currentRoute = this.router.url;
    return currentRoute.startsWith(link!) && ((link!.length === 1 && link === currentRoute) || link!.length > 1);
  }

  isBlockedLink(requiredTenant: boolean = false, requiredDeployment: boolean = false): boolean {
    if (requiredTenant && isNullOrEmpty(this.deploymentStore.selectedTenant())) {
      return true;
    }
    return !!(requiredDeployment && isNullOrEmpty(this.deploymentStore.selectedDeployment()));
  }

  getBlockedMessage(requiredTenant: boolean = false, requiredDeployment: boolean = false): string {
    if (requiredTenant && isNullOrEmpty(this.deploymentStore.selectedTenant())) {
      return 'Required tenant not set';
    }
    if (requiredDeployment && isNullOrEmpty(this.deploymentStore.selectedDeployment())) {
      return 'Required deployment not set';
    }
    return '';
  }

  protected readonly isNotNullOrEmpty = isNotNullOrEmpty;
}
